import type { IBaseContentMedia } from '@/services/isomorphic/ContentService';
import type { IContentMediaItem } from '../../template-data/IContentMedia';

/**
 * Converts the `IBaseContentMedia` to an `IContentMediaItem` so that it can be rendered.
 * @param media - The converted media item from a datamodel.
 * @returns The content media object for rendering.
 */
export const transformBaseMediaComponent = (
  media: IBaseContentMedia
): IContentMediaItem => {
  const { videoSettings } = media;
  return {
    item: media.media,
    aspectRatio: media.aspectRatio ?? '1:1',
    videoSettings: {
      poster: null,
      autoplay: videoSettings?.autoplay ?? false,
      controls: videoSettings?.controls ?? true,
      loop: videoSettings?.loop ?? true,
      muted: videoSettings?.muted ?? true
    },
    featured: false,
    gravity: 'center',
    hoverStyle: 'none',
    overlay: false,
    overlayColor: ''
  };
};

/**
 * Type representing categorized media items by device type.
 */
interface IMediaList {
  /**
   * Array of media items for desktop devices.
   */
  desktop: Array<IContentMediaItem>;
  /**
   * Array of media items for mobile devices.
   */
  mobile: Array<IContentMediaItem>;
  /**
   * Array of media items for tablet devices.
   */
  tablet: Array<IContentMediaItem>;
}

/**
 * Categorizes media items by their device type.
 * @param media - Media to categorize by device type.
 * @returns An object containing arrays of media items categorized by device type.
 */
export const getMediaList = (media: Array<IBaseContentMedia>): IMediaList => {
  const acc: IMediaList = { desktop: [], mobile: [], tablet: [] };
  media.forEach((mediaItem) => {
    acc[mediaItem.device].push(transformBaseMediaComponent(mediaItem));
  });

  return acc;
};
